/**
 * MoreActionsSheet — bottom-sheet style dialog for less-frequent actions.
 *
 * Replaces the modal "Special Actions" dialog from EstadisticasNew.js with
 * something less intrusive: it slides up from the bottom and leaves the
 * video visible above so the scout doesn't lose context.
 *
 * Action set mirrors the original "special actions" set:
 *   STEAL, BLOCK, TURNOVER, FOUL_RECEIVED, SCREEN, TIMEOUT, TECHNICAL,
 *   CHARGE_TAKEN, CHARGE_DRAWN, DEFLECTION
 */

import React from 'react';
import { Drawer, Box, Typography, IconButton, Avatar } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useStatsTheme } from '../theme/statsTheme';
import { ACTION_CODES } from '../../utils/actionUtils';
import ActionButton from './ActionButton';

const SPECIAL_ACTIONS = [
  { code: ACTION_CODES.STEAL, label: 'STL', variant: 'neutral' },
  { code: ACTION_CODES.BLOCK, label: 'BLK', variant: 'info' },
  { code: ACTION_CODES.TURNOVER, label: 'TO', variant: 'missed' },
  { code: ACTION_CODES.FOUL_RECEIVED, label: 'FR', variant: 'warning' },
  { code: ACTION_CODES.SCREEN, label: 'SCR', variant: 'neutral' },
  { code: ACTION_CODES.TIMEOUT, label: 'T/O', variant: 'neutral' },
  { code: ACTION_CODES.TECHNICAL, label: 'TECH', variant: 'opponent' },
  { code: ACTION_CODES.CHARGE_TAKEN, label: 'CHG+', variant: 'info' },
  { code: ACTION_CODES.CHARGE_DRAWN, label: 'CHG-', variant: 'info' },
  { code: ACTION_CODES.DEFLECTION, label: 'DFL', variant: 'neutral' },
];

export default function MoreActionsSheet({ open, onClose, player, onAction, t }) {
  const theme = useStatsTheme();
  const tt = (key, fallback) => (typeof t === 'function' ? t(`estadisticasNew.${key}`, { defaultValue: fallback }) : fallback);

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          backgroundColor: theme.surfaces.elevated,
          borderTopLeftRadius: theme.radius.xl,
          borderTopRightRadius: theme.radius.xl,
          borderTop: `1px solid ${theme.accentBorder}`,
          boxShadow: theme.shadows.lg,
          color: theme.text.primary,
          maxHeight: '60vh',
        },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 4,
          backgroundColor: theme.surfaces.divider,
          borderRadius: 2,
          alignSelf: 'center',
          mt: 1,
          mb: 1.5,
        }}
      />

      <Box sx={{ px: 2, pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          {player && (
            <Avatar
              src={player.avatarUrl}
              sx={{
                width: 36,
                height: 36,
                border: `1px solid ${theme.accentBorder}`,
                backgroundColor: theme.surfaces.floating,
              }}
            >
              {player.nom?.charAt(0).toUpperCase() || '?'}
            </Avatar>
          )}
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 11, color: theme.text.secondary, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              {tt('moreActions', 'More actions')}
            </Typography>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: theme.text.primary }}>
              {player?.nom || ''}
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="close" sx={{ color: theme.text.secondary }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gap: 1,
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(3, 1fr)',
              md: 'repeat(5, 1fr)',
            },
          }}
        >
          {SPECIAL_ACTIONS.map(({ code, label, variant }) => (
            <ActionButton
              key={code}
              variant={variant}
              label={tt(`actionCodes.${code}`, label)}
              size="md"
              fullWidth
              onClick={() => {
                if (typeof onAction === 'function' && player) onAction(player, code, 0);
                onClose && onClose();
              }}
            />
          ))}
        </Box>
      </Box>
    </Drawer>
  );
}
